import clsx from 'clsx';
import React, {
  MouseEvent, useCallback, useEffect, useRef, useState
} from 'react';
import {
  FiChevronDown,
  FiCodepen,
  FiCodesandbox,
  FiLogOut,
  FiSettings
} from 'react-icons/fi';
import { MdOutlineAnalytics } from 'react-icons/md';
import { RiContractLine } from 'react-icons/ri';
import { SiReadthedocs } from 'react-icons/si';
import { Link, useLocation } from 'react-router-dom';

import { useFlag } from '@/components/flags/provider';
import { showSidebarAuditLogs, showSidebarUsers } from '@/flags';
import { useAuthContext } from '@/hooks/useAuthContext';
import { envVarsProvider } from '@/utils/envVars/envVars';
import { settingsTabPath, SettingsTab } from '@/utils/secretsLink';

const DOCS_BASE_URL = envVarsProvider.get( 'DOCS_BASE_URL' );
import styles from './sidebar.module.scss';

interface NavItem {
  label: string;
  path: string;
  // When set, the item links to `path?tab=<tab>` and is active iff the URL's
  // `tab` search param matches. Used to disambiguate the three /settings tabs.
  tab?: SettingsTab;
  external?: boolean;
  flagKey?: 'showSidebarUsers' | 'showSidebarAuditLogs';
}

interface NavGroup {
  key: string;
  label: string;
  icon: React.ComponentType;
  items: NavItem[];
  matchPaths: string[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    key: 'agents',
    label: 'Agents',
    icon: FiCodesandbox,
    matchPaths: [ '/agents', '/prompts', '/logs' ],
    items: [
      { label: 'Manage', path: '/agents' },
      { label: 'Prompts', path: '/prompts' },
      { label: 'Logs', path: '/logs' }
    ]
  },
  {
    key: 'models',
    label: 'Models',
    icon: FiCodepen,
    matchPaths: [ '/models' ],
    items: [
      { label: 'Manage', path: '/models' }
    ]
  },
  {
    key: 'evals',
    label: 'Evals',
    icon: MdOutlineAnalytics,
    matchPaths: [ '/evaluations' ],
    items: [
      { label: 'Manage', path: '/evaluations' },
      { label: 'Metrics', path: '/evaluations/metrics' },
      { label: 'Sources', path: '/evaluations/sources' }
    ]
  },
  {
    key: 'data',
    label: 'Data',
    icon: RiContractLine,
    matchPaths: [ '/dataCatalog' ],
    items: [
      { label: 'Data Sources', path: '/dataCatalog' },
      { label: 'Explorer', path: '/dataCatalog/explorer' }
    ]
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: FiSettings,
    matchPaths: [ '/settings', '/admin', '/audit' ],
    items: [
      { label: 'API Keys', path: '/settings', tab: 'apiKeys' },
      { label: 'Secrets', path: '/settings', tab: 'secrets' },
      { label: 'Settings', path: '/settings', tab: 'settings' },
      { label: 'Users', path: '/admin', flagKey: 'showSidebarUsers' },
      { label: 'Audit Logs', path: '/audit', flagKey: 'showSidebarAuditLogs' }
    ]
  }
];

function isGroupActive( group: NavGroup, pathname: string ): boolean {
  return group.matchPaths.some( prefix => pathname.startsWith( prefix ) );
}

function isItemActive( item: NavItem, pathname: string, search: string ): boolean {
  // For the first item in evaluations, exact match to avoid matching /evaluations/metrics
  if ( item.path === '/evaluations' ) {
    return pathname === '/evaluations'
      || pathname.startsWith( '/evaluations/compare' )
      || pathname.startsWith( '/evaluations/results' )
      || pathname.startsWith( '/evaluations/runs' );
  }

  // For dataCatalog root, match exact and sub-routes but not /dataCatalog/explorer
  if ( item.path === '/dataCatalog' ) {
    return pathname === '/dataCatalog'
      || pathname.startsWith( '/dataCatalog/details' )
      || pathname.startsWith( '/dataCatalog/create' );
  }

  // For /admin, avoid matching /admin2 etc.
  if ( item.path === '/admin' ) {
    return pathname.startsWith( '/admin' ) && !pathname.startsWith( '/admin2' );
  }

  // The /settings tabs share the same pathname, so disambiguate by the tab
  // search param. When tab is missing the apiKeys item wins (matches the
  // SettingsPage default).
  if ( item.path === '/settings' && pathname.startsWith( '/settings' ) ) {
    const currentTab = new URLSearchParams( search ).get( 'tab' );
    if ( item.tab ) {
      return currentTab === item.tab
        || ( !currentTab && item.tab === 'apiKeys' );
    }
  }

  return pathname.startsWith( item.path );
}

function buildItemHref( item: NavItem ): string {
  return item.tab ? settingsTabPath( item.tab ) : item.path;
}

export function Sidebar() {
  const { pathname, search } = useLocation();
  const { logout, sessionContext } = useAuthContext();

  const canShowSidebarUsers = useFlag( showSidebarUsers );
  const canShowSidebarAuditLogs = useFlag( showSidebarAuditLogs );

  const flagMap: Record<string, boolean> = {
    showSidebarUsers: canShowSidebarUsers,
    showSidebarAuditLogs: canShowSidebarAuditLogs
  };

  const [ showNav, setShowNav ] = useState( false );
  const [ expandedGroup, setExpandedGroup ] = useState<string | null>( null );
  const [ hoveredGroup, setHoveredGroup ] = useState<string | null>( null );
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null );

  useEffect( () => {
    setShowNav( !!sessionContext );
  }, [ showNav, sessionContext ] );

  // Auto-expand the group matching the current route
  useEffect( () => {
    const activeGroup = NAV_GROUPS.find( g => isGroupActive( g, pathname ) );
    if ( activeGroup ) {
      setExpandedGroup( activeGroup.key );
    }
  }, [ pathname ] );

  const onClickLogout = async( e: MouseEvent<HTMLAnchorElement> ) => {
    e.preventDefault();
    await logout();
  };

  const toggleGroup = ( key: string ) => {
    setExpandedGroup( prev => ( prev === key ? null : key ) );
  };

  const handleGroupMouseEnter = useCallback( ( key: string ) => {
    if ( hoverTimeoutRef.current ) {
      clearTimeout( hoverTimeoutRef.current );
    }
    hoverTimeoutRef.current = setTimeout( () => {
      setHoveredGroup( key );
    }, 150 );
  }, [] );

  const handleGroupMouseLeave = useCallback( () => {
    if ( hoverTimeoutRef.current ) {
      clearTimeout( hoverTimeoutRef.current );
    }
    hoverTimeoutRef.current = setTimeout( () => {
      setHoveredGroup( null );
    }, 200 );
  }, [] );

  const handlePopoverMouseEnter = useCallback( () => {
    if ( hoverTimeoutRef.current ) {
      clearTimeout( hoverTimeoutRef.current );
    }
  }, [] );

  const handlePopoverMouseLeave = useCallback( () => {
    if ( hoverTimeoutRef.current ) {
      clearTimeout( hoverTimeoutRef.current );
    }
    hoverTimeoutRef.current = setTimeout( () => {
      setHoveredGroup( null );
    }, 200 );
  }, [] );

  useEffect( () => {
    return () => {
      if ( hoverTimeoutRef.current ) {
        clearTimeout( hoverTimeoutRef.current );
      }
    };
  }, [] );

  const sidebarRef = useRef<HTMLElement>( null );

  return (
    showNav && (
      <aside ref={ sidebarRef } className={ styles.sidebar }>
        <div className={ styles.logo }>
          <Link
            to="/"
            title="home"
            className={ clsx( styles.navLink, pathname === '/' && styles.active ) }
          >
            <span className={ styles.iconWrapper }>
              <img alt="AIDP Studio Logo" src="logo.svg" />
            </span>
            <span className={ styles.label }>KanpAI™</span>
          </Link>
        </div>

        <nav className={ styles.navGroups }>
          { NAV_GROUPS.map( group => {
            const GroupIcon = group.icon;
            const groupActive = isGroupActive( group, pathname );
            const isExpanded = expandedGroup === group.key;
            const isHovered = hoveredGroup === group.key && !isExpanded;

            const visibleItems = group.items.filter(
              item => !item.flagKey || flagMap[item.flagKey]
            );

            if ( visibleItems.length === 0 ) return null;

            return (
              <div
                key={ group.key }
                className={ styles.group }
                onMouseLeave={ handleGroupMouseLeave }
              >
                <button
                  type="button"
                  className={ clsx( styles.groupHeader, groupActive && styles.groupActive ) }
                  onClick={ () => toggleGroup( group.key ) }
                  onMouseEnter={ () => handleGroupMouseEnter( group.key ) }
                  title={ group.label }
                >
                  <span className={ clsx( styles.iconWrapper, isExpanded && styles.iconWrapperExpanded ) }>
                    <span className={ styles.iconDefault }><GroupIcon /></span>
                    <FiChevronDown className={ clsx( styles.chevron, isExpanded && styles.chevronOpen ) } />
                  </span>
                  <span className={ styles.label }>{ group.label }</span>
                </button>

                { isExpanded && (
                  <div className={ styles.subItems }>
                    { visibleItems.map( item => (
                      <Link
                        key={ `${ item.path }:${ item.tab ?? '' }` }
                        to={ buildItemHref( item ) }
                        className={ clsx(
                          styles.subItem,
                          isItemActive( item, pathname, search ) && styles.subItemActive
                        ) }
                      >
                        { item.label }
                      </Link>
                    ) ) }
                  </div>
                ) }

                { isHovered && (
                  <div
                    className={ styles.hoverPopover }
                    onMouseEnter={ handlePopoverMouseEnter }
                    onMouseLeave={ handlePopoverMouseLeave }
                  >
                    { visibleItems.map( item => (
                      <Link
                        key={ `${ item.path }:${ item.tab ?? '' }` }
                        to={ buildItemHref( item ) }
                      >
                        { item.label }
                      </Link>
                    ) ) }
                  </div>
                ) }
              </div>
            );
          } ) }

          <a
            href={ DOCS_BASE_URL }
            title="docs"
            target="_blank"
            className={ styles.navLink }
          >
            <span className={ styles.iconWrapper }>
              <SiReadthedocs />
            </span>
            <span className={ styles.label }>Docs</span>
          </a>
        </nav>

        <div className={ styles.bottom }>
          <Link
            to="/"
            onClick={ onClickLogout }
            title="logout"
            className={ styles.navLink }
          >
            <span className={ styles.iconWrapper }>
              <FiLogOut />
            </span>
            <span className={ styles.label }>Logout</span>
          </Link>
        </div>
      </aside>
    )
  );
}
